import { strFromU8, unzipSync } from "fflate";

/** A parsed .pptx: archive entry path → raw bytes (XML/rels parts only). */
type PptxArchive = Record<string, Uint8Array>;

// OOXML Namespaces
const NS_P = "http://schemas.openxmlformats.org/presentationml/2006/main";
const NS_A = "http://schemas.openxmlformats.org/drawingml/2006/main";
const NS_R = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const NS_DGM = "http://schemas.openxmlformats.org/drawingml/2006/diagram";
// A <a:graphicData> with this uri identifies a SmartArt diagram graphic frame.
const DIAGRAM_URI = "http://schemas.openxmlformats.org/drawingml/2006/diagram";

/** A single extracted shape (text box, table, etc.) from a slide. */
export interface ExtractedShape {
  name: string;
  text: string;
  paragraphs: string[];
  isTable: boolean;
  cellGrid?: string[][];
}

/** Per-slide extraction result. */
export interface ExtractedSlide {
  slideIndex: number;
  slideNumber: number;
  shapes: ExtractedShape[];
  fullText: string;
}

/**
 * Find a child element by localName, with namespace-aware lookup first,
 * then fallback to scanning by localName (handles namespace resolution quirks).
 */
function _findChild(parent: Element | null, nsUri: string, localName: string): Element | null {
  if (!parent) return null;

  // Try namespace-aware lookup first
  const nsList = parent.getElementsByTagNameNS(nsUri, localName);
  if (nsList.length > 0) return nsList[0] ?? null;

  // Fallback: scan children by localName
  for (const el of parent.getElementsByTagName("*")) {
    if (el.localName === localName) return el;
  }
  return null;
}

/**
 * Find all child elements by localName, with namespace-aware lookup first,
 * then fallback to scanning by localName.
 */
function _findAll(parent: Element | null, nsUri: string, localName: string): Element[] {
  if (!parent) return [];

  // Try namespace-aware lookup first
  const nsList = parent.getElementsByTagNameNS(nsUri, localName);
  if (nsList.length > 0) return Array.from(nsList);

  // Fallback: scan by localName
  const results: Element[] = [];
  for (const el of parent.getElementsByTagName("*")) {
    if (el.localName === localName) results.push(el);
  }
  return results;
}

// Placeholder shape names that are metadata, not user content.
// These change between files (date stamps, slide numbers) and cause false positives.
const PLACEHOLDER_NAMES = new Set<string>([
  "slide number placeholder", "date placeholder", "footer placeholder",
  "header placeholder", "filler text",
]);

// Placeholder type indices (from <p:ph type="..." />) that are metadata, not content.
const PLACEHOLDER_TYPES = new Set<string>([
  "sldNum", "dt", "ftr", "hdr",
]);

/**
 * Check whether a shape element is a metadata placeholder (date, slide number, footer).
 * @param sp — the <p:sp> element
 */
function _isMetadataPlaceholder(sp: Element): boolean {
  // Check nvSpPr → nvPr → ph type
  const nvSpPr = sp.getElementsByTagNameNS(NS_P, "nvSpPr")[0];
  if (nvSpPr) {
    const nvPr = nvSpPr.getElementsByTagNameNS(NS_P, "nvPr")[0] ||
                 _findChild(nvSpPr, NS_P, "nvPr");
    if (nvPr) {
      const ph = nvPr.getElementsByTagNameNS(NS_P, "ph")[0] ||
                 _findChild(nvPr, NS_P, "ph");
      if (ph) {
        const phType = ph.getAttribute("type") || "";
        if (PLACEHOLDER_TYPES.has(phType)) return true;
      }
    }
  }
  return false;
}

/**
 * Extract text paragraphs from a <p:txBody> or <a:txBody> element.
 * Returns { text, paragraphs } where paragraphs is an array of trimmed
 * paragraph strings and text is the joined result.
 *
 * IMPORTANT: Skips <a:fld> (field) elements — these are auto-generated
 * metadata (date stamps, slide numbers, page counts) that differ between
 * files and produce false positives in comparisons.
 */
function _extractTextFromTxBody(txBody: Element): { text: string; paragraphs: string[] } {
  const paragraphs: string[] = [];

  const pElements = _findAll(txBody, NS_A, "p");
  for (const pEl of pElements) {
    let paraText = "";

    // Runs: <a:r> → <a:t>  (user-authored text only, skip <a:fld>)
    const runs = _findAll(pEl, NS_A, "r");
    for (const run of runs) {
      const tElements = _findAll(run, NS_A, "t");
      for (const t of tElements) {
        paraText += t.textContent || "";
      }
    }

    // NOTE: <a:fld> elements intentionally skipped — they contain auto-generated
    // values (e.g. "4/8/2026", "Slide 3") that change between file saves and
    // create noise in the comparison.

    if (paraText.trim()) {
      paragraphs.push(paraText);  // preserve original whitespace for accurate diffing
    }
  }

  return { text: paragraphs.join("\n"), paragraphs };
}

/**
 * Extract the shape name from a non-visual properties element.
 * Tries p:cNvPr, a:cNvPr, then bare localName "cNvPr".
 * @param nvPr - e.g. <p:nvSpPr> or <p:nvGrpSpPr>
 */
function _getShapeName(nvPr: Element | null): string {
  if (!nvPr) return "";
  let cNvPr: Element | undefined = nvPr.getElementsByTagNameNS(NS_P, "cNvPr")[0];
  cNvPr ??= nvPr.getElementsByTagNameNS(NS_A, "cNvPr")[0];
  if (!cNvPr) {
    const all = nvPr.getElementsByTagName("cNvPr");
    if (all.length > 0) cNvPr = all[0];
  }
  return cNvPr?.getAttribute("name") ?? "";
}

/** Locate a shape's <p:txBody>/<a:txBody> element (localName fallback). */
function _findTxBody(sp: Element): Element | undefined {
  const nsBody = sp.getElementsByTagNameNS(NS_P, "txBody")[0] || sp.getElementsByTagNameNS(NS_A, "txBody")[0];
  if (nsBody) return nsBody;
  for (const el of sp.getElementsByTagName("*")) {
    if (el.localName === "txBody") return el;
  }
  return undefined;
}

/**
 * Extract shapes from a single <p:sp> element.
 * Skips metadata placeholders (date, slide number, footer) and shapes
 * with no meaningful text content (only shapes with real text are returned).
 */
function _extractShape(sp: Element): ExtractedShape | null {
  // Resolved before the work below so the catch can name the offending shape.
  let name = "";
  try {
    // Skip metadata placeholder shapes entirely — they produce false positives
    if (_isMetadataPlaceholder(sp)) return null;

    name = _getShapeName(_findChild(sp, NS_P, "nvSpPr"));

    // Skip shapes whose names indicate metadata placeholders
    if (name && PLACEHOLDER_NAMES.has(name.toLowerCase())) return null;

    const txBody = _findTxBody(sp);
    if (!txBody) return null;

    const { text, paragraphs } = _extractTextFromTxBody(txBody);
    // Skip shapes with no meaningful text (empty or whitespace-only)
    if (!text.trim()) return null;

    return { name, text, paragraphs, isTable: false };
  } catch (err) {
    const suffix = name ? ` "${name}"` : "";
    console.warn(
      `[pptxTextExtractor] Error extracting shape${suffix}:`,
      err instanceof Error ? err.message : err,
    );
    return null;
  }
}

/**
 * Direct <p:{localName}> children of `parent`. getElementsByTagNameNS returns
 * ALL descendants, so we keep only those whose parentNode is `parent` — this is
 * what stops a group from double-counting its nested groups' children.
 */
function _directChildren(parent: Element, localName: string): Element[] {
  return _findAll(parent, NS_P, localName).filter((el) => el.parentNode === parent);
}

/**
 * Recursively extract shapes from a group shape (<p:grpSp>).
 * Group shapes can contain <p:sp>, nested <p:grpSp>, and <p:graphicFrame> (tables).
 */
function _extractGroupShapes(grpSp: Element): ExtractedShape[] {
  const results: ExtractedShape[] = [];
  let groupName = "";

  try {
    groupName = _getShapeName(_findChild(grpSp, NS_P, "nvGrpSpPr"));

    // Direct <p:sp> children inside the group
    for (const sp of _directChildren(grpSp, "sp")) {
      const shape = _extractShape(sp);
      if (shape) results.push(shape);
    }

    // Recursively handle nested group shapes
    for (const nested of _directChildren(grpSp, "grpSp")) {
      results.push(..._extractGroupShapes(nested));
    }

    // Tables inside the group
    for (const gf of _directChildren(grpSp, "graphicFrame")) {
      const tableShape = _extractTableFromGraphicFrame(gf);
      if (tableShape) results.push(tableShape);
    }
  } catch (err) {
    const suffix = groupName ? ` "${groupName}"` : "";
    console.warn(
      `[pptxTextExtractor] Error extracting group shape${suffix}:`,
      err instanceof Error ? err.message : err,
    );
  }

  return results;
}

/**
 * Extract table data from a <p:graphicFrame> element containing an <a:tbl>.
 * Serializes as tab-separated rows in text, with individual rows as paragraphs.
 */
/** Trimmed text of each <a:tc> cell in a table row (<a:tr>). */
function _rowCellTexts(row: Element): string[] {
  return _findAll(row, NS_A, "tc").map((cell) => {
    let cellText = "";
    for (const t of _findAll(cell, NS_A, "t")) cellText += (t.textContent || "") + " ";
    return cellText.trim();
  });
}

function _extractTableFromGraphicFrame(graphicFrame: Element): ExtractedShape | null {
  // Resolved before the work below so the catch can name the offending table.
  let name = "";
  try {
    const tbl = _findChild(graphicFrame, NS_A, "tbl");
    if (!tbl) return null;

    name = _getShapeName(_findChild(graphicFrame, NS_P, "nvGraphicFramePr"));

    const rows = _findAll(tbl, NS_A, "tr");
    if (rows.length === 0) return null;

    // Row/col grid drives cell-level comparison; paragraphs/text are tab/newline joins of it.
    const cellGrid: string[][] = rows.map(_rowCellTexts);
    const paragraphs = cellGrid.map((cells) => cells.join("\t"));
    const text = paragraphs.join("\n");
    if (!text.trim() && !name) return null;

    return { name: name || "(table)", text, paragraphs, isTable: true, cellGrid };
  } catch (err) {
    const suffix = name ? ` "${name}"` : "";
    console.warn(
      `[pptxTextExtractor] Error extracting table${suffix}:`,
      err instanceof Error ? err.message : err,
    );
    return null;
  }
}

/**
 * Extract all shapes (sp, grpSp, tables) from a parsed slide XML document.
 */
/**
 * Extract shapes from all top-level groups, marking their descendant
 * sp/graphicFrame elements as `processed` so phases 2–3 don't re-count them.
 */
function _collectGroupShapes(slideDoc: Document, processed: Set<Element>): ExtractedShape[] {
  const shapes: ExtractedShape[] = [];
  for (const grpSp of slideDoc.getElementsByTagNameNS(NS_P, "grpSp")) {
    // Only process top-level groups (skip nested ones — they're handled recursively)
    if ((grpSp.parentNode as Element | null)?.localName === "grpSp") continue;

    shapes.push(..._extractGroupShapes(grpSp));

    // Mark all descendant sp/graphicFrame elements as processed
    for (const el of grpSp.getElementsByTagNameNS(NS_P, "sp")) processed.add(el);
    for (const el of grpSp.getElementsByTagNameNS(NS_P, "graphicFrame")) processed.add(el);
  }
  return shapes;
}

function _extractAllShapesFromSlide(slideDoc: Document): ExtractedShape[] {
  // Track elements we've already processed via groups to avoid duplicates
  const processed = new Set<Element>();

  // 1. Process group shapes first (recursively extracts their children)
  const shapes: ExtractedShape[] = _collectGroupShapes(slideDoc, processed);

  // 2. Process regular shapes (p:sp) not inside groups
  for (const sp of slideDoc.getElementsByTagNameNS(NS_P, "sp")) {
    if (processed.has(sp)) continue;
    const shape = _extractShape(sp);
    if (shape) shapes.push(shape);
  }

  // 3. Process tables in graphicFrames not inside groups
  for (const gf of slideDoc.getElementsByTagNameNS(NS_P, "graphicFrame")) {
    if (processed.has(gf)) continue;
    const tableShape = _extractTableFromGraphicFrame(gf);
    if (tableShape) shapes.push(tableShape);
  }

  return shapes;
}

/** Resolve a relationship Target (may be relative, e.g. "../diagrams/data1.xml") against a base dir. */
function _resolvePath(baseDir: string, target: string): string {
  if (target.startsWith("/")) return target.slice(1);
  const parts = baseDir.split("/").filter(Boolean);
  for (const seg of target.split("/")) {
    if (seg === "..") parts.pop();
    else if (seg !== "." && seg !== "") parts.push(seg);
  }
  return parts.join("/");
}

/**
 * Collect the text of a SmartArt diagram data part (`ppt/diagrams/dataN.xml`).
 * Each `<dgm:pt>` node's text becomes one paragraph; presentation/background
 * points carry no `<dgm:t>` and are skipped naturally.
 */
function _diagramText(dataDoc: Document): { text: string; paragraphs: string[] } {
  const paragraphs: string[] = [];
  for (const pt of Array.from(dataDoc.getElementsByTagNameNS(NS_DGM, "pt"))) {
    const t = _findChild(pt, NS_DGM, "t");
    if (!t) continue;
    let ptText = "";
    for (const at of _findAll(t, NS_A, "t")) ptText += at.textContent || "";
    if (ptText.trim()) paragraphs.push(ptText);
  }
  return { text: paragraphs.join("\n"), paragraphs };
}

/**
 * Parse one graphic frame as SmartArt, or return null if it isn't one (or has
 * no resolvable/parseable diagram text). SmartArt is a `<p:graphicFrame>` whose
 * `<a:graphicData>` uri is the diagram namespace; its text lives in a separate
 * diagram data part referenced by `<dgm:relIds r:dm="…">` through the slide's
 * own .rels.
 */
function _smartArtShape(
  gf: Element,
  zip: PptxArchive,
  slideDir: string,
  slideRelMap: Record<string, string>,
): ExtractedShape | null {
  const graphicData = _findChild(gf, NS_A, "graphicData");
  if (!graphicData || graphicData.getAttribute("uri") !== DIAGRAM_URI) return null;

  const relIds = gf.getElementsByTagNameNS(NS_DGM, "relIds")[0];
  if (!relIds) return null;
  const dm = relIds.getAttributeNS(NS_R, "dm") || relIds.getAttribute("r:dm");
  if (!dm) return null;

  const target = slideRelMap[dm];
  if (!target) return null;
  const dataBytes = zip[_resolvePath(slideDir, target)];
  if (!dataBytes) return null;

  const dataDoc = new DOMParser().parseFromString(strFromU8(dataBytes), "text/xml");
  if (dataDoc.querySelector("parsererror")) return null;

  const { text, paragraphs } = _diagramText(dataDoc);
  if (!text.trim()) return null;

  const name = _getShapeName(_findChild(gf, NS_P, "nvGraphicFramePr")) || "(smartart)";
  return { name, text, paragraphs, isTable: false };
}

/**
 * Extract SmartArt shapes on a slide. Scans every graphic frame (including
 * grouped ones) — non-diagram frames yield null, so this never conflicts with
 * table extraction.
 */
function _extractSmartArtShapes(
  slideDoc: Document,
  zip: PptxArchive,
  slideDir: string,
  slideRelMap: Record<string, string>,
): ExtractedShape[] {
  const out: ExtractedShape[] = [];
  for (const gf of Array.from(slideDoc.getElementsByTagNameNS(NS_P, "graphicFrame"))) {
    try {
      const shape = _smartArtShape(gf, zip, slideDir, slideRelMap);
      if (shape) out.push(shape);
    } catch (err) {
      console.warn("[pptxTextExtractor] Error extracting SmartArt:", err instanceof Error ? err.message : err);
    }
  }
  return out;
}

/** Ordered slide relationship ids from presentation.xml's <p:sldIdLst>. */
function _slideRIds(presDoc: Document): string[] {
  const sldIdElements = presDoc.getElementsByTagNameNS(NS_P, "sldId");
  const slideRIds: string[] = [];
  for (let i = 0; i < sldIdElements.length; i++) {
    const sldId = sldIdElements[i];
    if (!sldId) continue;
    const rId = sldId.getAttributeNS(NS_R, "id") || sldId.getAttribute("r:id");
    if (rId) slideRIds.push(rId);
    else console.warn(`[pptxTextExtractor] Slide ${i + 1} has no r:id, skipping`);
  }
  return slideRIds;
}

/** Build a map of relationship id → target path from a .rels document. */
function _buildRelMap(relsDoc: Document): Record<string, string> {
  const relMap: Record<string, string> = {};
  for (const rel of relsDoc.getElementsByTagName("Relationship")) {
    const id = rel.getAttribute("Id");
    const target = rel.getAttribute("Target");
    if (id && target) relMap[id] = target;
  }
  return relMap;
}

/** Parse one slide's XML and extract its shapes + concatenated fullText. */
function _extractOneSlide(
  zip: PptxArchive,
  relMap: Record<string, string>,
  rId: string,
  idx: number,
): ExtractedSlide {
  const slideNumber = idx + 1;
  const empty: ExtractedSlide = { slideIndex: idx, slideNumber, shapes: [], fullText: "" };

  const target = relMap[rId];
  if (!target) {
    console.warn(`[pptxTextExtractor] No target found for rId=${rId} (slide ${slideNumber})`);
    return empty;
  }

  // Target is relative to ppt/ folder (e.g. "slides/slide1.xml")
  const slideBytes = zip["ppt/" + target];
  if (!slideBytes) {
    console.warn(`[pptxTextExtractor] Slide file not found: ppt/${target}`);
    return empty;
  }

  const slideDoc = new DOMParser().parseFromString(strFromU8(slideBytes), "text/xml");

  const parseError = slideDoc.querySelector("parsererror");
  if (parseError) {
    console.warn(`[pptxTextExtractor] XML parse error on slide ${slideNumber}:`, (parseError.textContent || "").substring(0, 200));
    return empty;
  }

  const shapes = _extractAllShapesFromSlide(slideDoc);

  // SmartArt text lives in a separate diagram data part reached through the
  // slide's own .rels, so resolve those and append any diagram shapes.
  const slidePath = "ppt/" + target;
  const slash = slidePath.lastIndexOf("/");
  const slideDir = slidePath.slice(0, slash);
  const slideRelsBytes = zip[`${slideDir}/_rels/${slidePath.slice(slash + 1)}.rels`];
  if (slideRelsBytes) {
    const slideRelsDoc = new DOMParser().parseFromString(strFromU8(slideRelsBytes), "text/xml");
    if (!slideRelsDoc.querySelector("parsererror")) {
      shapes.push(..._extractSmartArtShapes(slideDoc, zip, slideDir, _buildRelMap(slideRelsDoc)));
    }
  }

  // Concatenate all shape text into fullText for similarity matching
  const fullText = shapes.map((s) => s.text).filter(Boolean).join("\n\n");
  console.log(`[pptxTextExtractor] Slide ${slideNumber}: ${shapes.length} shapes, ${fullText.length} chars`);
  return { slideIndex: idx, slideNumber, shapes, fullText };
}

/**
 * Standalone PPTX text extractor — parses any .pptx ArrayBuffer and extracts
 * text from all slides. No Office JS dependency.
 *
 * @param arrayBuffer — raw .pptx file content
 */
export async function extractAllSlidesText(arrayBuffer: ArrayBuffer): Promise<ExtractedSlide[]> {
  console.log("[pptxTextExtractor] extractAllSlidesText() called");

  // Decompress the XML parts only (skip media/fonts) — the diff is text-based.
  const zip: PptxArchive = unzipSync(new Uint8Array(arrayBuffer), {
    filter: (f) => f.name.endsWith(".xml") || f.name.endsWith(".rels"),
  });

  // ---- Step 1: Read presentation.xml to get ordered slide r:ids ----
  const presBytes = zip["ppt/presentation.xml"];
  if (!presBytes) {
    throw new Error("ppt/presentation.xml not found in PPTX archive.");
  }
  const presDoc = new DOMParser().parseFromString(strFromU8(presBytes), "text/xml");

  const slideRIds = _slideRIds(presDoc);
  console.log("[pptxTextExtractor] Found", slideRIds.length, "slide IDs");
  if (slideRIds.length === 0) {
    console.warn("[pptxTextExtractor] No slides found in presentation.xml");
    return [];
  }

  // ---- Step 2: Read presentation.xml.rels to resolve r:ids → slide paths ----
  const relsBytes = zip["ppt/_rels/presentation.xml.rels"];
  if (!relsBytes) {
    throw new Error("ppt/_rels/presentation.xml.rels not found in PPTX archive.");
  }
  const relsDoc = new DOMParser().parseFromString(strFromU8(relsBytes), "text/xml");
  const relMap = _buildRelMap(relsDoc);

  // ---- Step 3-5: For each slide, parse XML and extract shapes/text ----
  const results: ExtractedSlide[] = [];
  for (let idx = 0; idx < slideRIds.length; idx++) {
    const rId = slideRIds[idx];
    if (!rId) continue;
    try {
      results.push(_extractOneSlide(zip, relMap, rId, idx));
    } catch (slideErr) {
      console.error(`[pptxTextExtractor] Error processing slide ${idx + 1}:`, slideErr instanceof Error ? slideErr.message : slideErr);
      results.push({ slideIndex: idx, slideNumber: idx + 1, shapes: [], fullText: "" });
    }
  }

  console.log(`[pptxTextExtractor] Extraction complete: ${results.length} slides processed`);
  return results;
}
